import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, Type, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const [textSize, setTextSize] = useState("normal");
  const searchQuery = searchParams.get("q") || "";
  const listType = searchParams.get("type") || "search";
  const filterType = searchParams.get("filter") || "";
  const selectedDate = searchParams.get("date") || "";

  // Mock data - replace with actual API call
  const mockResults = [
    {
      id: "1",
      suitNumber: "ID/8598GCMW/22",
      parties: "MR. EMEM SUNDAY & ORS VS. GOVERNOR OF LAGOS",
      classification: "GENERAL CIVIL",
      judgeName: "HON. JUSTICE K.O. ALOGBA",
      date: "TUESDAY 14TH OCTOBER, 2025",
      courtRoom: "COURT 1",
      status: "FURTHER HEARING",
    },
    {
      id: "2",
      suitNumber: "ID/ADR/2763/23",
      parties: "MR. LAWRENCE ODEY OTIALA VS. GUARANTY TRUST BANK PLC & ANR",
      classification: "GENERAL CIVIL",
      judgeName: "HON. JUSTICE K.O. ALOGBA",
      date: "TUESDAY 14TH OCTOBER, 2025",
      courtRoom: "COURT 1",
      status: "FURTHER HEARING",
    },
    {
      id: "3",
      suitNumber: "ID/1721/10",
      parties: "MR. RAPHAEL ABANUM VS. MR. JOSEPH KEHINDE OYEBANJI",
      classification: "GENERAL CIVIL",
      judgeName: "HON. JUSTICE K.O. ALOGBA",
      date: "TUESDAY 14TH OCTOBER, 2025",
      courtRoom: "COURT 1",
      status: "PENDING APPLICATION",
    },
    {
      id: "4",
      suitNumber: "ID/ADR/2536/23",
      parties: "CHIEF NNAMDI AGBALUGO VS. MS. BOLUWATITO FAMOROTI",
      classification: "LAND MATTERS",
      judgeName: "HON. JUSTICE K.O. ALOGBA",
      date: "FRIDAY 17TH OCTOBER, 2025",
      courtRoom: "COURT 1",
      status: "PENDING APPLICATION",
    },
    {
      id: "5",
      suitNumber: "ID/7225GCM/23",
      parties: "JOISE INTERNATIONAL LTD & ANR VS. GASTRONOMIX COMPANY LTD",
      classification: "GENERAL CIVIL",
      judgeName: "HON. JUSTICE K.O. ALOGBA",
      date: "TUESDAY 14TH OCTOBER, 2025",
      courtRoom: "COURT 1",
      status: "	CONTINUATION",
    },
  ];

  const adjustTextSize = (size) => {
    setTextSize(size);
  };

  const getFontSizeClass = () => {
    if (textSize === "large") return "text-lg";
    if (textSize === "small") return "text-sm";
    return "text-base";
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("hearing"))
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    if (statusLower.includes("judgment"))
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    if (statusLower.includes("mention"))
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    if (statusLower.includes("adjournment"))
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Implement CSV/PDF download functionality
    alert("Download functionality would be implemented here");
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <CompactHeader />
      {/* Accessibility Controls Bar */}
      <div className="bg-[#1E293B] text-white py-3 px-4 sticky top-[100px] z-40">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          {/* Text Size Controls */}
          <div className="flex items-center gap-3">
            <Type className="w-5 h-5" aria-hidden="true" />
            <div
              className="flex gap-2"
              role="group"
              aria-label="Text size controls"
            >
              <button
                onClick={() => adjustTextSize("small")}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === "small"
                    ? "bg-white text-[#1E293B]"
                    : "bg-white/10 hover:bg-white/20"
                }`}
                aria-label="Small text size"
                aria-pressed={textSize === "small"}
              >
                A-
              </button>
              <button
                onClick={() => adjustTextSize("normal")}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === "normal"
                    ? "bg-white text-[#1E293B]"
                    : "bg-white/10 hover:bg-white/20"
                }`}
                aria-label="Normal text size"
                aria-pressed={textSize === "normal"}
              >
                A
              </button>
              <button
                onClick={() => adjustTextSize("large")}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === "large"
                    ? "bg-white text-[#1E293B]"
                    : "bg-white/10 hover:bg-white/20"
                }`}
                aria-label="Large text size"
                aria-pressed={textSize === "large"}
              >
                A+
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button Section */}
      <div className="bg-gray-50 dark:bg-slate-800 py-4 px-4 border-b border-gray-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/CauseLists"
            className="inline-flex items-center gap-2 text-[#155dfc] hover:text-[#6B1515] transition-colors focus:outline-none focus:ring-2 focus:ring-[#155dfc]/50 rounded-lg px-3 py-2"
            aria-label="Go back to Cause List"
          >
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            <span className={getFontSizeClass()}>Back to Cause List</span>
          </Link>
        </div>
      </div>

      {/* Header Section with Gradient */}
      <section className="relative py-24 bg-hero-light-image dark:bg-hero-dark">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-6">
            <h1
              className={`${getFontSizeClass()} mb-3 text-gray-900 drop-shadow-sm`}
            >
              {listType === "daily" && "Daily List Results"}
              {listType === "advance" && "Advance List Results"}
              {listType === "supplementary" && "Supplementary List Results"}
              {listType === "search" && "Search Results"}
            </h1>
            {searchQuery ? (
              <p
                className={`${getFontSizeClass()} text-gray-800 drop-shadow-sm`}
              >
                Showing results for:{" "}
                <span className="font-semibold">"{searchQuery}"</span>
              </p>
            ) : (
              <div
                className={`${getFontSizeClass()} text-gray-800 drop-shadow-sm space-y-1`}
              >
                {filterType && (
                  <p>
                    Filter:{" "}
                    <span className="font-semibold capitalize">
                      {filterType.replace("-", " ")}
                    </span>
                  </p>
                )}
                {selectedDate && (
                  <p>
                    Date:{" "}
                    <span className="font-semibold">
                      {format(new Date(selectedDate), "PPP")}
                    </span>
                  </p>
                )}
              </div>
            )}
            <p
              className={`${getFontSizeClass()} text-gray-700 mt-2 drop-shadow-sm`}
            >
              {mockResults.length} case(s) found
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="bg-white/90 hover:bg-white border-2 border-[#155dfc]/30 text-gray-900"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Results
            </Button>
            <Button
              onClick={handleDownload}
              variant="outline"
              className="bg-white/90 hover:bg-white border-2 border-[#155dfc]/30 text-gray-900"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </section>

      {/* Results Table Section */}
      <section className="py-12 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="table-fixed w-full">
                <TableHeader>
                  <TableRow className="bg-[#155dfc] hover:bg-[#155dfc]">
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      SUIT NUMBER
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      PARTIES
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      CLASSIFICATION
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      JUDGE NAME
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      DATE
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      COURT ROOM
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      STATUS
                    </TableHead>
                    <TableHead className={`table-head ${getFontSizeClass()}`}>
                      ACTIONS
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockResults.map((result, index) => (
                    <TableRow
                      key={result.id}
                      className={`${
                        index % 2 === 0
                          ? "bg-gray-50 dark:bg-slate-700"
                          : "bg-white dark:bg-slate-800"
                      } hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors`}
                    >
                      <TableCell
                        className={`${getFontSizeClass()} font-medium text-[#155dfc] break-words max-w-[120px]`}
                      >
                        {result.suitNumber}
                      </TableCell>

                      <TableCell
                        className={`${getFontSizeClass()} break-words max-w-xs whitespace-normal`}
                      >
                        {result.parties}
                      </TableCell>

                      <TableCell
                        className={`${getFontSizeClass()} break-words max-w-[120px]`}
                      >
                        <Badge variant="outline" className="whitespace-nowrap">
                          {result.classification}
                        </Badge>
                      </TableCell>

                      <TableCell
                        className={`${getFontSizeClass()} break-words max-w-sm whitespace-normal`}
                      >
                        {result.judgeName}
                      </TableCell>

                      <TableCell
                        className={`${getFontSizeClass()} whitespace-nowrap`}
                      >
                        {result.date}
                      </TableCell>

                      <TableCell
                        className={`${getFontSizeClass()} text-center break-words max-w-[80px]`}
                      >
                        {result.courtRoom}
                      </TableCell>

                      <TableCell>
                        <Badge className={getStatusColor(result.status)}>
                          {result.status}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#155dfc] hover:text-[#6B1515] hover:bg-[#155dfc]/10"
                          aria-label={`View details for case ${result.suitNumber}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* No Results Message - show if no results */}
          {mockResults.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-12 max-w-2xl mx-auto border-2 border-gray-200 dark:border-slate-700">
                <h3
                  className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-4`}
                >
                  No Results Found
                </h3>
                <p
                  className={`${getFontSizeClass()} text-gray-600 dark:text-gray-400 mb-6`}
                >
                  We couldn't find any cases matching your search query "
                  {searchQuery}".
                </p>
                <Link to="/cause-list">
                  <Button className="bg-[#155dfc] hover:bg-[#6B1515]">
                    Return to Cause List
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
